/**
 * Status Field Type - Common
 *
 * Type-registry configuration, field-type configuration, and the shared
 * live bindings (getTypeConfig / resolveFieldConfig) used by the inline
 * (inline/status.tsx) and modal (modal/status.tsx) renderers.
 *
 * The `*Fn` bindings are ES-module live bindings: `initDependencies` calls the
 * setters, and the renderers (which import the bindings from this module)
 * observe the updated values.
 */

import type { FieldTypeConfig, TypeConfig } from '../../components/types';
import { S } from './columnWidths';
import { StatusColors } from './columnColors';

// ============================================================================
// Type Registry Configuration
// ============================================================================

/**
 * Status type configuration for the type registry
 * Displays Active/Inactive for boolean values
 */
export const statusTypeConfig: TypeConfig = {
  options: [
    { id: true, label: 'Active' },
    { id: false, label: 'Inactive' },
  ] as const,
  chipColors: StatusColors,
};

// ============================================================================
// Shared live bindings (set lazily to avoid circular dependencies)
// ============================================================================

// Store reference to getTypeConfig to avoid circular dependencies (modal renderer)
// eslint-disable-next-line import/no-mutable-exports
export let getTypeConfigFn: ((type: string) => TypeConfig | undefined) | null = null;

/**
 * Set the getTypeConfig function (called from initDependencies to avoid circular deps)
 */
export const setGetTypeConfig = (fn: (type: string) => TypeConfig | undefined): void => {
  getTypeConfigFn = fn;
};

// Store reference to resolveFieldConfig to avoid circular dependencies (inline renderers)
// eslint-disable-next-line import/no-mutable-exports
export let resolveFieldConfigFn: ((col: { id: string; type?: string }) => unknown) | null = null;

/**
 * Set the resolveFieldConfig function (called from initDependencies to avoid circular deps)
 */
export const setResolveFieldConfig = (
  fn: (col: { id: string; type?: string }) => unknown
): void => {
  resolveFieldConfigFn = fn;
};

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Status field type configuration
 */
export const statusFieldType: FieldTypeConfig = {
  placeholder: (label: string) => `Select ${label.toLowerCase()}...`,
  ...S,
};
